import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  Archive,
  ArrowLeft,
  BriefcaseBusiness,
  ChevronDown,
  Download,
  FileText,
  Inbox,
  Landmark,
  LoaderCircle,
  LogOut,
  Mail,
  MailOpen,
  Paperclip,
  PenLine,
  RefreshCw,
  Reply,
  Search,
  Send,
  ShieldCheck,
  Trash2,
  UserRound,
  Wrench,
  X,
} from "lucide-react";
import { ALIASES, Alias, api, MessageDetail, MessageSummary, Session } from "./api";

type Folder = "inbound" | "sent";

interface ComposeState {
  from: Alias;
  to: string;
  subject: string;
  text: string;
  replyToMessageRowId?: string;
}

const aliasMeta: Record<Alias, { label: string; short: string; icon: typeof Landmark; tone: string }> = {
  "finance@themago.com": { label: "Finance", short: "财务", icon: Landmark, tone: "ochre" },
  "business@themago.com": { label: "Business", short: "商务", icon: BriefcaseBusiness, tone: "blue" },
  "service@themago.com": { label: "Service", short: "服务", icon: Wrench, tone: "green" },
};

function formatTime(timestamp: number | null, long = false): string {
  if (!timestamp) return "—";
  const date = new Date(timestamp * 1000);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  if (!long && sameDay) return date.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" });
  return date.toLocaleString("zh-CN", long
    ? { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" }
    : { month: "2-digit", day: "2-digit" });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function parseAddresses(value: string): string[] {
  try {
    const parsed = JSON.parse(value) as string[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function avatarText(message: MessageSummary | MessageDetail): string {
  const source = message.direction === "sent"
    ? parseAddresses(message.to_json)[0] || "S"
    : message.from_name || message.from_address;
  return source.trim().charAt(0).toUpperCase() || "M";
}

function safeHtmlDocument(html: string): string {
  const clean = DOMPurify.sanitize(html, {
    FORBID_TAGS: ["script", "style", "form", "input", "button", "iframe", "object", "embed", "svg"],
    FORBID_ATTR: ["srcset", "onerror", "onclick", "onload"],
  });
  const parser = new DOMParser();
  const document = parser.parseFromString(clean, "text/html");
  document.querySelectorAll("img").forEach((image) => {
    const source = image.getAttribute("src") ?? "";
    if (!source.startsWith("data:")) {
      image.removeAttribute("src");
      image.setAttribute("alt", image.getAttribute("alt") || "[外部图片已屏蔽]");
    }
  });
  document.querySelectorAll("a").forEach((link) => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });
  return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>
    :root{color-scheme:light}body{margin:0;padding:28px;color:#202735;background:#fff;font:15px/1.72 Arial,sans-serif;overflow-wrap:anywhere}
    img{max-width:100%;height:auto}a{color:#4f46e5}blockquote{border-left:3px solid #d7dce5;margin-left:0;padding-left:16px;color:#667085}
    table{max-width:100%;border-collapse:collapse}pre{white-space:pre-wrap}
  </style></head><body>${document.body.innerHTML}</body></html>`;
}

function Login({ onLogin }: { onLogin: (session: Session) => void }) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      onLogin(await api.login(username, password));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "登录失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-shell">
        <div className="login-brand">
          <span className="brand-mark"><Mail size={18} /></span>
          <div><strong>The Mago Mail</strong><span>themago.com</span></div>
        </div>
        <form className="login-card" onSubmit={submit}>
          <div className="secure-pill"><ShieldCheck size={14} /> 私人邮件空间</div>
          <h1>欢迎回来</h1>
          <p className="muted">登录以查看和管理 themago.com 的邮件</p>
          <label>
            <span>账号</span>
            <div className="field-with-icon"><UserRound size={18} /><input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" placeholder="请输入账号" /></div>
          </label>
          <label>
            <span>密码</span>
            <div className="field-with-icon"><ShieldCheck size={18} /><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" placeholder="请输入密码" autoFocus /></div>
          </label>
          {error && <div className="form-error">{error}</div>}
          <button className="primary full" disabled={busy}>
            {busy ? <LoaderCircle className="spin" size={18} /> : "登录"}
          </button>
        </form>
        <div className="secure-note"><ShieldCheck size={14} /> 加密连接 · 安全会话</div>
      </section>
    </main>
  );
}

function Compose({ initial, sendingEnabled, onClose, onSent }: {
  initial: ComposeState;
  sendingEnabled: boolean;
  onClose: () => void;
  onSent: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function send(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.send(draft);
      onSent();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "发送失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="compose-pane">
      <header className="reader-toolbar">
        <button className="icon-button" onClick={onClose} title="关闭"><X size={19} /></button>
        <div className="toolbar-spacer" />
        <span className="draft-state">新邮件</span>
      </header>
      <form className="compose-form" onSubmit={send}>
        <div className="compose-heading">
          <p className="eyebrow">COMPOSE</p>
          <h2>{draft.replyToMessageRowId ? "回复邮件" : "写一封新邮件"}</h2>
        </div>
        {!sendingEnabled && <div className="notice">发信密钥尚未配置。收信功能不受影响，配置 Resend API Key 后即可发送。</div>}
        <label className="compose-row">
          <span>发件人</span>
          <div className="select-wrap">
            <select value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value as Alias })}>
              {ALIASES.map((item) => <option key={item}>{item}</option>)}
            </select>
            <ChevronDown size={16} />
          </div>
        </label>
        <label className="compose-row">
          <span>收件人</span>
          <input value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} placeholder="name@example.com" />
        </label>
        <label className="compose-row">
          <span>主题</span>
          <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} placeholder="邮件主题" />
        </label>
        <textarea className="compose-body" value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} placeholder="开始写邮件……" autoFocus />
        {error && <div className="form-error">{error}</div>}
        <footer className="compose-actions">
          <button type="button" className="text-button" onClick={onClose}>取消</button>
          <button className="primary" disabled={busy || !sendingEnabled}>
            {busy ? <LoaderCircle className="spin" size={17} /> : <Send size={16} />} 发送邮件
          </button>
        </footer>
      </form>
    </section>
  );
}

function Reader({ message, loading, onReply, onDelete, onUnread }: {
  message: MessageDetail | null;
  loading: boolean;
  onReply: (message: MessageDetail) => void;
  onDelete: (message: MessageDetail) => void;
  onUnread: (message: MessageDetail) => void;
}) {
  const [mode, setMode] = useState<"text" | "html">("text");
  useEffect(() => setMode("text"), [message?.id]);

  if (loading) return <section className="reader empty"><LoaderCircle className="spin" size={25} /></section>;
  if (!message) {
    return (
      <section className="reader empty">
        <div className="empty-orbit"><MailOpen size={30} strokeWidth={1.4} /></div>
        <h3>选择一封邮件阅读</h3>
        <p>邮件内容将在这里安全显示</p>
      </section>
    );
  }

  const timestamp = message.received_at ?? message.sent_at;
  const recipients = parseAddresses(message.to_json);
  return (
    <section className="reader">
      <header className="reader-toolbar">
        {message.direction === "inbound" && <button className="icon-button" onClick={() => onReply(message)} title="回复"><Reply size={18} /></button>}
        {message.direction === "inbound" && <button className="icon-button" onClick={() => onUnread(message)} title="标为未读"><Mail size={18} /></button>}
        <button className="icon-button danger-hover" onClick={() => onDelete(message)} title="删除"><Trash2 size={18} /></button>
        <div className="toolbar-spacer" />
        {message.html_body && <div className="view-toggle"><button className={mode === "text" ? "active" : ""} onClick={() => setMode("text")}>纯文本</button><button className={mode === "html" ? "active" : ""} onClick={() => setMode("html")}>HTML</button></div>}
      </header>
      <article className="message-content">
        <div className="message-title-row">
          <div>
            <p className="mailbox-kicker">{aliasMeta[message.mailbox].label} · {message.direction === "sent" ? "SENT" : "INBOX"}</p>
            <h1>{message.subject}</h1>
          </div>
          {message.has_attachments ? <Paperclip size={19} className="soft-icon" /> : null}
        </div>
        <div className="sender-block">
          <div className={`avatar ${aliasMeta[message.mailbox].tone}`}>{avatarText(message)}</div>
          <div className="sender-lines">
            <strong>{message.direction === "sent" ? recipients.join(", ") : message.from_name || message.from_address}</strong>
            <span>{message.direction === "sent" ? `发自 ${message.mailbox}` : `${message.from_address} → ${message.mailbox}`}</span>
          </div>
          <time>{formatTime(timestamp, true)}</time>
        </div>

        {message.body_truncated ? <div className="notice compact">正文过大，页面仅显示前一部分；完整内容保存在原始邮件中。</div> : null}
        {mode === "html" && message.html_body
          ? <iframe className="html-frame" sandbox="" srcDoc={safeHtmlDocument(message.html_body)} title="邮件正文" />
          : <div className="plain-body">{message.text_body || "（邮件没有纯文本正文，请切换到 HTML 查看）"}</div>}

        {message.attachments.length > 0 && (
          <section className="attachments">
            <div className="section-label"><Paperclip size={15} /> 附件 · {message.attachments.length}</div>
            <div className="attachment-grid">
              {message.attachments.map((attachment) => (
                <a key={attachment.id} className="attachment" href={`/api/attachments/${attachment.id}`}>
                  <div className="file-icon"><FileText size={20} /></div>
                  <div><strong>{attachment.filename}</strong><span>{formatBytes(attachment.size)}</span></div>
                  <Download size={17} />
                </a>
              ))}
            </div>
          </section>
        )}
      </article>
    </section>
  );
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [booting, setBooting] = useState(true);
  const [folder, setFolder] = useState<Folder>("inbound");
  const [alias, setAlias] = useState("");
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState<MessageSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<MessageDetail | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [compose, setCompose] = useState<ComposeState | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.session().then(setSession).catch(() => setSession(null)).finally(() => setBooting(false));
  }, []);

  const loadMessages = useCallback(async () => {
    if (!session) return;
    setLoadingList(true);
    setError("");
    try {
      const result = await api.messages(folder, alias, query);
      setMessages(result.messages);
      if (selectedId && !result.messages.some((item) => item.id === selectedId)) {
        setSelectedId(null);
        setSelected(null);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "无法加载邮件");
    } finally {
      setLoadingList(false);
    }
  }, [session, folder, alias, query, selectedId]);

  useEffect(() => {
    const timeout = window.setTimeout(loadMessages, query ? 250 : 0);
    return () => window.clearTimeout(timeout);
  }, [loadMessages, query]);

  async function openMessage(id: string) {
    setCompose(null);
    setSelectedId(id);
    setLoadingMessage(true);
    try {
      const result = await api.message(id);
      setSelected(result.message);
      setMessages((current) => current.map((item) => item.id === id ? { ...item, is_read: 1 } : item));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "无法读取邮件");
    } finally {
      setLoadingMessage(false);
    }
  }

  function switchFolder(next: Folder, nextAlias = "") {
    setFolder(next);
    setAlias(nextAlias);
    setSelectedId(null);
    setSelected(null);
    setCompose(null);
  }

  function startCompose(from: Alias = ALIASES[0]) {
    setSelectedId(null);
    setSelected(null);
    setCompose({ from, to: "", subject: "", text: "" });
  }

  function replyTo(message: MessageDetail) {
    setCompose({
      from: message.mailbox,
      to: message.reply_to || message.from_address,
      subject: /^re:/i.test(message.subject) ? message.subject : `Re: ${message.subject}`,
      text: "",
      replyToMessageRowId: message.id,
    });
  }

  async function removeMessage(message: MessageDetail) {
    if (!window.confirm(`确定删除“${message.subject}”吗？此操作无法撤销。`)) return;
    await api.remove(message.id);
    setSelected(null);
    setSelectedId(null);
    await loadMessages();
  }

  async function markUnread(message: MessageDetail) {
    await api.mark(message.id, false);
    setSelected(null);
    setSelectedId(null);
    await loadMessages();
  }

  const unreadCount = useMemo(() => messages.filter((item) => item.direction === "inbound" && !item.is_read).length, [messages]);
  const heading = folder === "sent" ? "已发送" : alias ? aliasMeta[alias as Alias].short : "收件箱";

  if (booting) return <div className="boot"><span className="brand-mark"><Mail size={17} /></span><LoaderCircle className="spin" size={18} /></div>;
  if (!session) return <Login onLogin={setSession} />;

  return (
    <main className="mail-app">
      <aside className="sidebar">
        <div className="brand"><span className="brand-mark"><Mail size={17} /></span><span>The Mago</span></div>
        <button className="compose-button" onClick={() => startCompose()}><PenLine size={17} /> 写邮件</button>
        <nav className="main-nav">
          <button className={folder === "inbound" && !alias ? "active" : ""} onClick={() => switchFolder("inbound")}>
            <Inbox size={18} /><span>收件箱</span>{unreadCount > 0 && <b>{unreadCount}</b>}
          </button>
          <button className={folder === "sent" ? "active" : ""} onClick={() => switchFolder("sent")}>
            <Send size={17} /><span>已发送</span>
          </button>
        </nav>
        <div className="nav-section">
          <div className="nav-label">邮箱地址</div>
          {ALIASES.map((item) => {
            const meta = aliasMeta[item];
            const Icon = meta.icon;
            return (
              <button key={item} className={`alias-link ${folder === "inbound" && alias === item ? "active" : ""}`} onClick={() => switchFolder("inbound", item)}>
                <span className={`alias-icon ${meta.tone}`}><Icon size={15} /></span>
                <span><strong>{meta.label}</strong><small>{item}</small></span>
              </button>
            );
          })}
        </div>
        <div className="sidebar-bottom">
          <div className="account"><span>A</span><div><strong>{session.username}</strong><small>管理员</small></div></div>
          <button className="icon-button" title="退出" onClick={async () => { await api.logout(); setSession(null); }}><LogOut size={17} /></button>
        </div>
      </aside>

      <section className="message-list-panel">
        <header className="list-header">
          <div><p className="eyebrow">MAILBOX</p><h2>{heading}</h2></div>
          <button className="icon-button" onClick={loadMessages} title="刷新"><RefreshCw className={loadingList ? "spin" : ""} size={17} /></button>
        </header>
        <div className="search-box"><Search size={17} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="搜索邮件" />{query && <button onClick={() => setQuery("")}><X size={15} /></button>}</div>
        {error && <div className="list-error">{error}</div>}
        <div className="message-scroll">
          {!loadingList && messages.length === 0 && <div className="list-empty"><Archive size={25} /><span>这里还没有邮件</span></div>}
          {messages.map((message) => {
            const name = message.direction === "sent"
              ? parseAddresses(message.to_json).join(", ")
              : message.from_name || message.from_address;
            return (
              <button key={message.id} className={`message-item ${selectedId === message.id ? "selected" : ""} ${!message.is_read ? "unread" : ""}`} onClick={() => openMessage(message.id)}>
                <div className="item-top"><span className={`avatar small ${aliasMeta[message.mailbox].tone}`}>{avatarText(message)}</span><strong>{name}</strong><time>{formatTime(message.received_at ?? message.sent_at)}</time></div>
                <div className="item-subject">{message.subject}</div>
                <div className="item-preview">{message.preview || "（无文本内容）"}</div>
                <div className="item-bottom"><span className={`alias-pill ${aliasMeta[message.mailbox].tone}`}>{aliasMeta[message.mailbox].short}</span>{message.has_attachments ? <Paperclip size={14} /> : null}{!message.is_read && <i />}</div>
              </button>
            );
          })}
        </div>
      </section>

      {compose ? (
        <Compose
          initial={compose}
          sendingEnabled={session.sendingEnabled}
          onClose={() => setCompose(null)}
          onSent={() => { setCompose(null); switchFolder("sent"); }}
        />
      ) : (
        <Reader message={selected} loading={loadingMessage} onReply={replyTo} onDelete={removeMessage} onUnread={markUnread} />
      )}
    </main>
  );
}
